import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CreditCard, ShoppingCart, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import CheckoutAuthModal from "@/components/checkout/CheckoutAuthModal";

export interface CartItem {
  id: string;
  name: string;
  price: string | number; // can be "19,99" (comma) or cents as number (1999)
  description: string;
  type?: 'subscription' | 'service'; // Add type to distinguish between subscriptions and services
  isUpgrade?: boolean; // Indicates if this is an upgrade from current plan
  isPeriodChange?: boolean; // Indicates if this is a period change (month <-> year)
  currentPlanPrice?: string | number; // Current plan price for upgrade difference calculation (formatted as string or cents)
  period?: 'month' | 'year'; // Billing period for subscriptions
  stripePriceId?: string | null; // Stripe price ID for checkout
  isManualSubscription?: boolean; // Indicates if the user has a manual subscription
  currentPlanEndDate?: string; // End date of current manual subscription
  isTrialEligible?: boolean; // Indicates if user qualifies for 1€ trial month
}

interface CartContentProps {
  cartItem: CartItem | null;
  onRemoveItem: () => void;
  onClose?: () => void;
}

export default function CartContent({ cartItem, onRemoveItem, onClose }: CartContentProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const parsePrice = (p: string | number | undefined) => {
    if (!p) return 0;
    // If it's already a number, assume it's in cents and convert to euros
    if (typeof p === 'number') return p / 100;
    // If it's a string, parse it (could be "19,99" or "1999")
    const normalized = p.replace(/\./g, "").replace(",", ".");
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
  };

  // Calculate display price (1€ for trial eligible, otherwise full price)
  const displayPrice = useMemo(() => {
    if (cartItem?.isTrialEligible) {
      return 1; // 1€ trial price
    }
    return parsePrice(cartItem?.price);
  }, [cartItem]);
  
  const originalPrice = useMemo(() => parsePrice(cartItem?.price), [cartItem]);
  const vat = 0; // Always 0% - no VAT collected
  const total = useMemo(() => displayPrice, [displayPrice]);

  const calculateUpgradeDifference = useMemo(() => {
    if (!cartItem?.isUpgrade || !cartItem?.currentPlanPrice) {
      return null;
    }
    
    const newPrice = parsePrice(cartItem.price);
    const currentPrice = parsePrice(cartItem.currentPlanPrice);
    const difference = newPrice - currentPrice;
    
    return difference > 0 ? difference : null;
  }, [cartItem]);

  const handleCheckout = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!cartItem) {
      toast({
        title: "Warenkorb leer",
        description: "Fügen Sie erst ein Produkt zu Ihrem Warenkorb hinzu.",
        variant: "destructive",
      });
      return;
    }

    await proceedToStripeCheckout();
  };

  const proceedToStripeCheckout = async () => {
    if (!cartItem) return;

    setLoading(true);
    try {
      let data, error;
      
      if (cartItem.type === 'service') {
        // For services, use the service payment endpoint
        ({ data, error } = await supabase.functions.invoke("create-service-payment", {
          body: { 
            priceId: cartItem.id,
            serviceName: cartItem.name 
          },
        }));
      } else {
        // For subscriptions, use the checkout endpoint
        ({ data, error } = await supabase.functions.invoke("create-checkout", {
          body: { 
            planType: cartItem.id,
            stripePriceId: cartItem.stripePriceId
          },
        }));
      }
      
      if (error) throw error;

      // All responses should now contain a URL for Stripe checkout
      if (data.url) {
        const newWindow = window.open(data.url, "_blank");
        if (!newWindow) window.location.href = data.url;

        toast({
          title: "Weiterleitung zu Stripe",
          description:
            "Sie werden zu unserem sicheren Zahlungspartner weitergeleitet. Ihre Rechnungsdaten werden dort erfasst.",
        });
        onClose?.();
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast({
        title: "Fehler beim Checkout",
        description: "Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    // After successful auth, proceed to checkout
    proceedToStripeCheckout();
  };

  return (
    <>
      <div className="w-full">
        {!cartItem ? (
          <div className="p-6 text-center">
            <ShoppingCart className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Warenkorb ist leer</p>
            <p className="text-xs text-muted-foreground mt-1">Wählen Sie einen Plan aus</p>
          </div>
        ) : (
          <div className="p-4">
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-base truncate">{cartItem.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{cartItem.description}</p>
                  <Badge variant="secondary" className="mt-2 text-xs">
                    {cartItem.type === 'service' 
                      ? 'Einmalzahlung' 
                      : cartItem.period === 'year' 
                        ? 'Jährlich' 
                        : 'Monatlich'}
                  </Badge>
                </div>
                <div className="text-right ml-3 flex-shrink-0">
                  {cartItem.isTrialEligible ? (
                    <>
                      <p className="text-sm text-muted-foreground line-through">€{originalPrice.toFixed(2)}</p>
                      <p className="text-lg font-bold text-primary">€1,00</p>
                      <p className="text-xs text-primary font-medium">Erster Monat</p>
                    </>
                  ) : (
                    <>
                      <p className="text-lg font-bold">€{displayPrice.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">
                        {cartItem.type === 'service' 
                          ? 'einmalig' 
                          : cartItem.period === 'year' 
                            ? 'pro Jahr' 
                            : 'pro Monat'}
                      </p>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRemoveItem}
                    className="mt-1 text-destructive hover:text-destructive p-1"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="space-y-2 text-sm">
              {(cartItem.isUpgrade || cartItem.isPeriodChange) && calculateUpgradeDifference ? (
                <>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Aktueller Plan:</span>
                    <span>€{parsePrice(cartItem.currentPlanPrice).toFixed(2)}/{cartItem.period === 'year' ? 'Jahr' : 'Monat'}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Neuer Plan:</span>
                    <span>€{parsePrice(cartItem.price).toFixed(2)}/{cartItem.period === 'year' ? 'Jahr' : 'Monat'}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-semibold text-base">
                    <span>{cartItem.isPeriodChange ? 'Preis-Differenz:' : 'Upgrade-Differenz:'}</span>
                    <span className="text-primary">+€{calculateUpgradeDifference.toFixed(2)}/{cartItem.period === 'year' ? 'Jahr' : 'Monat'}</span>
                  </div>
                  <div className="p-2 bg-primary/10 rounded text-xs">
                    <strong>Hinweis:</strong> {cartItem.isManualSubscription ? (
                      <>
                        Ihr aktueller Plan läuft bis {cartItem.currentPlanEndDate ? new Date(cartItem.currentPlanEndDate).toLocaleDateString('de-DE') : 'zum Monatsende'} weiter. Die neue Subscription beginnt automatisch danach. Sie zahlen jetzt: €{parsePrice(cartItem.price).toFixed(2)}/{cartItem.period === 'year' ? 'Jahr' : 'Monat'}.
                      </>
                    ) : (
                      <>
                        Sie zahlen im Stripe Checkout die anteilige Differenz für den Rest des {cartItem.period === 'year' ? 'Jahres' : 'Monats'}. Ab dem nächsten Abrechnungszeitraum: €{parsePrice(cartItem.price).toFixed(2)}/{cartItem.period === 'year' ? 'Jahr' : 'Monat'}.
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {cartItem.isTrialEligible && (
                    <div className="p-2 bg-primary/10 rounded-lg mb-3">
                      <p className="text-xs text-primary font-medium">
                        🎉 Sie erhalten den ersten Monat für nur 1€!
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Danach €{originalPrice.toFixed(2)}/Monat • Jederzeit kündbar
                      </p>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Zwischensumme:</span>
                    <span className="font-semibold">€{displayPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>MwSt. (0%):</span>
                    <span className="font-semibold">€0,00</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-base font-bold">
                    <span>Gesamtsumme:</span>
                    <span>€{displayPrice.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>

            <div className="mt-4 p-3 bg-primary/5 rounded-md">
              <p className="text-xs text-foreground/80">
                <strong>Hinweis:</strong> Rechnungsdaten werden sicher bei Stripe erfasst. Als Kleingewerbe erheben wir keine MwSt.
              </p>
            </div>

            <div className="pt-4">
              <Button onClick={handleCheckout} disabled={loading} className="w-full">
                {loading ? (
                  "Wird geladen..."
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    {user ? "Sicher bezahlen" : "Anmelden und bezahlen"}
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">Sicherer Checkout • SSL-verschlüsselt</p>
            </div>
          </div>
        )}
      </div>

      <CheckoutAuthModal
        open={showAuthModal}
        onOpenChange={setShowAuthModal}
        onSuccess={handleAuthSuccess}
      />
    </>
  );
}
